/**
 * AI Usage Analytics Types
 *
 * Type definitions for AI usage tracking and analytics dashboard
 */

export interface AIUsageSummary {
  totalGenerations: number;
  last7Days: {
    generations: number;
    successfulGenerations: number;
    tokensUsed: number;
    totalCost: number;
    successRate: number;
    cacheHitRate: number;
    avgResponseTime: number;
  };
  last30Days: {
    generations: number;
    tokensUsed: number;
    totalCost: number;
  };
}

export interface FeatureUsage {
  feature: string;
  count: number;
}

export interface TimelineDataPoint {
  date: string;
  provider: string;
  requests: number;
  successfulRequests: number;
  tokens: number;
  cost: number;
  avgResponseTime: number;
}

export interface RecentActivity {
  id: string;
  type: string;
  timestamp: Date;
  success: boolean;
  tokens: {
    prompt: number;
    completion: number;
    total: number;
  };
  cost: number;
  cached: boolean;
  responseTime: number;
  provider: string;
}

export interface AIUsageDashboard {
  summary: AIUsageSummary;
  featureUsage: FeatureUsage[];
  timeline: TimelineDataPoint[];
  recentActivity: RecentActivity[];
}

export interface AIUsageRecord {
  _id: string;
  userId: string;
  projectId?: string;
  provider: string;
  aiModel: string;
  requestType: string;
  tokens: {
    prompt: number;
    completion: number;
    total: number;
  };
  cost: number;
  cached: boolean;
  responseTime: number;
  success: boolean;
  errorType?: string;
  timestamp: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface AIUsageHistoryResponse {
  success: boolean;
  data: AIUsageRecord[];
  meta: {
    pagination: {
      page: number;
      limit: number;
      totalRecords: number;
      totalPages: number;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
    filters: {
      requestType?: string;
      success?: string;
      provider?: string;
      dateRange: {
        start?: string;
        end?: string;
      };
    };
    timestamp: string;
  };
}

export interface AIUsageStats {
  provider: string;
  model: string;
  requestType: string;
  totalRequests: number;
  successfulRequests: number;
  cachedRequests: number;
  successRate: number;
  cacheHitRate: number;
  totalTokens: number;
  totalPromptTokens: number;
  totalCompletionTokens: number;
  totalCost: number;
  avgResponseTime: number;
  minResponseTime: number;
  maxResponseTime: number;
}

export interface TimelineParams {
  timeframe?: 'hour' | 'day' | 'week' | 'month';
  days?: number;
  projectId?: string;
}

export interface HistoryParams {
  page?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
  requestType?: string;
  success?: boolean;
  provider?: string;
}

export interface StatsParams {
  timeframe?: 'day' | 'week' | 'month' | 'year';
}

// Chart data types for visualization
export interface ChartDataPoint {
  date: string;
  value: number;
  label?: string;
}

export interface AIUsageChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor?: string;
    borderColor?: string;
    fill?: boolean;
  }[];
}

// Request types enum for filtering
export enum AIRequestType {
  CHAT = 'chat',
  COMPLETION = 'completion',
  COMPONENT_GENERATION = 'component_generation',
  LAYOUT_OPTIMIZATION = 'layout_optimization',
  PROPERTY_MODIFICATION = 'property_modification',
  TEMPLATE_SELECTION = 'template_selection'
}

// Provider types
export enum AIProvider {
  OPENROUTER = 'openrouter',
  OPENAI = 'openai',
  ANTHROPIC = 'anthropic',
  GEMINI = 'gemini',
  GROK = 'grok'
}
